/**
 * Caller-owned Bookshelf and Account projections. These schemas intentionally
 * expose only persisted Profile, bookmark/progress, and published Work state.
 */
import { z } from "zod";
import { canonicalIdSchema } from "./content.ts";
import { sourceLanguageSchema, workStatusSchema } from "./work.ts";

const labelSchema = z.string().trim().min(1);
const avatarUrlSchema = z
  .union([
    z
      .url()
      .max(2_048)
      .refine((value) => /^https?:\/\//i.test(value)),
    z
      .string()
      .max(1_500_000)
      .regex(/^data:image\/(?:png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/)
  ])
  .nullable();

export const accountProfileSchema = z
  .object({
    userId: canonicalIdSchema,
    username: labelSchema,
    displayName: labelSchema.max(80),
    avatarUrl: avatarUrlSchema,
    preferredLanguage: z.enum(["en", "zh-CN"]),
    acceptsAiTranslation: z.boolean()
  })
  .strict();

export const accountProfileUpdateRequestSchema = z
  .object({
    displayName: labelSchema.max(80),
    avatarUrl: avatarUrlSchema
  })
  .strict();

export const accountProfileUpdateResponseSchema = z
  .object({
    profile: accountProfileSchema
  })
  .strict();

export const bookshelfItemSchema = z
  .object({
    workId: canonicalIdSchema,
    title: labelSchema,
    creator: labelSchema,
    sourceLanguage: sourceLanguageSchema,
    bookmarkedAt: z.iso.datetime({ offset: true }),
    progress: z
      .object({
        releaseVersionId: canonicalIdSchema,
        chapterId: canonicalIdSchema,
        segmentId: canonicalIdSchema,
        segmentIndex: z.number().int().nonnegative(),
        completed: z.boolean()
      })
      .strict()
      .nullable()
  })
  .strict();

export const bookshelfResponseSchema = z
  .object({
    items: z.array(bookshelfItemSchema).max(100),
    nextCursor: z.string().min(1).nullable()
  })
  .strict();

export const accountResponseSchema = z
  .object({
    profile: accountProfileSchema,
    ownedPublishedWorks: z
      .array(
        z
          .object({
            id: canonicalIdSchema,
            title: labelSchema,
            sourceLanguage: sourceLanguageSchema,
            status: workStatusSchema,
            publishedAt: z.iso.datetime({ offset: true })
          })
          .strict()
      )
      .max(100)
  })
  .strict();

export type BookshelfResponse = z.infer<typeof bookshelfResponseSchema>;
export type AccountResponse = z.infer<typeof accountResponseSchema>;
export type AccountProfileUpdateRequest = z.infer<
  typeof accountProfileUpdateRequestSchema
>;
export type AccountProfileUpdateResponse = z.infer<
  typeof accountProfileUpdateResponseSchema
>;
